import React, { useEffect, useState, useCallback } from 'react';
import {
  Activity, Loader2, ListTodo, Calendar, Target, Trophy, FileText, User as UserIcon,
  Plus, Check, RotateCcw, Edit3, Trash2,
} from 'lucide-react';

const KIND_ICONS = {
  todo: ListTodo,
  meeting: Calendar,
  project: Target,
  goal: Trophy,
  note: FileText,
  contact: UserIcon,
};

const ACTION_LABELS = {
  created: 'created',
  updated: 'updated',
  deleted: 'deleted',
  completed: 'completed',
  reopened: 'reopened',
};

const ACTION_ICONS = {
  created: Plus,
  updated: Edit3,
  deleted: Trash2,
  completed: Check,
  reopened: RotateCcw,
};

const ACTION_COLORS = {
  created: 'var(--accent)',
  updated: 'var(--gold)',
  deleted: 'var(--danger)',
  completed: 'var(--good)',
  reopened: 'var(--warn)',
};

export default function ActivityFeed({ api, compact = false, limit = 30 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const { data } = await api.get('/ceo-life/activity', { params: { limit } });
      setItems(data || []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  }, [api, limit]);

  useEffect(() => {
    fetch();
    const t = setInterval(fetch, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, [fetch]);

  if (loading) {
    return <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;
  }

  if (items.length === 0) {
    return (
      <div className={compact ? 'py-4 text-center' : 'ceo-empty'}>
        <Activity className="h-7 w-7 mx-auto mb-2 opacity-50" style={{ color: 'var(--text-low)' }} />
        <p className="text-xs" style={{ color: 'var(--text-low)' }}>No activity yet — start adding items and the feed will fill up here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="ceo-activity-feed">
      {items.map(a => (
        <ActivityRow key={a.id} a={a} />
      ))}
    </div>
  );
}

function ActivityRow({ a }) {
  const KIcon = KIND_ICONS[a.kind] || Activity;
  const AIcon = ACTION_ICONS[a.action] || Edit3;
  const aColor = ACTION_COLORS[a.action] || 'var(--text-dim)';
  const initials = (a.actor_name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="ceo-todo-row" style={{ alignItems: 'center' }} data-testid={`activity-row-${a.id}`}>
      {/* Avatar */}
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
        style={{ background: 'linear-gradient(135deg, var(--accent-2), var(--accent))', color: '#0F0F18' }}
        title={a.actor_name}
      >
        {initials || '?'}
      </div>

      {/* Action icon overlay */}
      <div className="flex items-center justify-center shrink-0" style={{
        width: 22, height: 22, borderRadius: 6, background: 'var(--ink-3)',
        border: `1px solid var(--line-2)`, marginLeft: -10, marginTop: 12, alignSelf: 'flex-start'
      }}>
        <AIcon className="h-3 w-3" style={{ color: aColor }} />
      </div>

      <div className="flex-1 min-w-0 ml-1">
        <p className="text-xs leading-snug" style={{ color: 'var(--text)' }}>
          <span style={{ fontWeight: 600 }}>{a.actor_name?.split(' ')[0] || 'Someone'}</span>
          <span style={{ color: 'var(--text-dim)' }}> {ACTION_LABELS[a.action] || a.action} </span>
          <span style={{ color: 'var(--text-low)' }}>{a.kind}</span>
          <span style={{ color: 'var(--text-dim)' }}> · </span>
          <span style={{ color: 'var(--text)' }}>"{a.target_label}"</span>
        </p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <KIcon className="h-2.5 w-2.5" style={{ color: 'var(--text-low)' }} />
          <span className="text-[10px]" style={{ color: 'var(--text-low)' }}>{formatRel(a.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

function formatRel(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 30) return 'just now';
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    if (diff < 86400*7) return `${Math.floor(diff/86400)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}
